import React, { useState, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import { getAuthHeaders, WORKER_URL } from '../../utils/api';

const STATUS_ORDER = {
    paid: { label: 'Pago', color: '#00a650' },
    pending: { label: 'Pendente', color: '#ff8000' },
    cancelled: { label: 'Cancelado', color: '#ff4444' },
    all: { label: 'Todos', color: '#2D3277' }
};

const SHIPPING_STATUS = {
    pending: 'Aguardando',
    handling: 'Em manuseio',
    ready_to_ship: 'Pronto p/ envio',
    shipped: 'Enviado',
    delivered: 'Entregue',
    not_delivered: 'Não entregue',
    cancelled: 'Cancelado'
};

const PERIODS = [
    { label: 'Hoje', days: 0 },
    { label: '7 dias', days: 7 },
    { label: '30 dias', days: 30 },
    { label: '90 dias', days: 90 },
];

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

const toDateStr = (date) => date.toISOString().split('T')[0];

function Vendas() {
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [period, setPeriod] = useState(7);
    const [statusFilter, setStatusFilter] = useState('paid');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [showCustom, setShowCustom] = useState(false);

    const getDateRange = useCallback(() => {
        if (showCustom && customFrom && customTo) {
            return { dateFrom: customFrom, dateTo: customTo };
        }
        const to = new Date();
        const from = new Date();
        from.setDate(to.getDate() - period);
        return { dateFrom: toDateStr(from), dateTo: toDateStr(to) };
    }, [period, showCustom, customFrom, customTo]);

    const fetchOrders = useCallback(async () => {
        const { dateFrom, dateTo } = getDateRange();
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ dateFrom, dateTo, status: statusFilter });
            const res = await fetch(`${WORKER_URL}/api/orders/history?${params}`, {
                headers: getAuthHeaders()
            });
            const data = await res.json();
            console.log('Vendas response:', data);
            setOrders(data.orders || []);
            setTotal(data.total || 0);
            setCount(data.count || 0);
        } catch (e) {
            console.error('Erro ao buscar vendas:', e);
        } finally {
            setIsLoading(false);
        }
    }, [getDateRange, statusFilter]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const visibleOrders = search
        ? orders.filter(o => {
            const q = search.toLowerCase();
            return o.id.toString().includes(q) || o.buyer.toLowerCase().includes(q) || o.title.toLowerCase().includes(q);
          })
        : orders;

    const ticketMedio = visibleOrders.length > 0
        ? visibleOrders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0) / visibleOrders.length
        : 0;
    const visibleTotal = visibleOrders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);

    return (
        <div style={{ padding: '20px' }}>
            <h2 style={{ color: '#2D3277', marginBottom: '16px' }}>Vendas</h2>

            {/* Métricas */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                {[
                    { label: 'Total do período', value: fmt(visibleTotal), color: '#00a650' },
                    { label: 'Pedidos', value: visibleOrders.length, color: '#2D3277' },
                    { label: 'Ticket médio', value: fmt(ticketMedio), color: '#ff8000' },
                ].map(m => (
                    <div key={m.label} style={{ backgroundColor: 'white', border: '1px solid #eee', borderRadius: '8px', padding: '12px 20px', flex: 1 }}>
                        <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '4px' }}>{m.label}</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: m.color }}>{m.value}</div>
                    </div>
                ))}
            </div>

            {/* Filtros */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Busca */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #ddd', borderRadius: '6px', padding: '5px 10px', minWidth: '240px' }}>
                    <Search size={15} color="#999" />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Pedido, comprador ou produto..."
                        style={{ border: 'none', outline: 'none', fontSize: '0.85rem', width: '100%' }}
                    />
                </div>

                {/* Período */}
                <div style={{ display: 'flex', gap: '6px' }}>
                    {PERIODS.map(p => (
                        <button key={p.days} onClick={() => { setPeriod(p.days); setShowCustom(false); }}
                            style={filterBtn(period === p.days && !showCustom)}>
                            {p.label}
                        </button>
                    ))}
                    <button onClick={() => setShowCustom(true)} style={filterBtn(showCustom)}>Personalizado</button>
                </div>

                {/* Status */}
                <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
                    {Object.entries(STATUS_ORDER).map(([key, val]) => (
                        <button key={key} onClick={() => setStatusFilter(key)} style={filterBtn(statusFilter === key)}>
                            {val.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Datas customizadas */}
            {showCustom && (
                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                    <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={dateInput} />
                    <span style={{ color: '#999' }}>até</span>
                    <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={dateInput} />
                    <button onClick={fetchOrders} style={{ backgroundColor: '#2D3277', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '0.8rem' }}>
                        Aplicar
                    </button>
                </div>
            )}

            {/* Tabela */}
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Carregando...</div>
            ) : visibleOrders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Nenhuma venda encontrada</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={th}>Data</th>
                            <th style={th}>Nº Pedido</th>
                            <th style={th}></th>
                            <th style={th}>Produto</th>
                            <th style={th}>Qtd</th>
                            <th style={th}>Comprador</th>
                            <th style={th}>Valor</th>
                            <th style={th}>Pagamento</th>
                            <th style={th}>Envio</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleOrders.map(order => (
                            <tr key={order.id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ ...td, color: '#666', whiteSpace: 'nowrap' }}>{fmtDate(order.date_created)}</td>
                                <td style={{ ...td, color: '#666' }}>#{order.id}</td>
                                <td style={{ ...td, width: '52px' }}>
                                    {order.thumbnail
                                        ? <img src={order.thumbnail} alt="" style={{ width: '44px', height: '44px', objectFit: 'contain', borderRadius: '4px' }} />
                                        : <div style={{ width: '44px', height: '44px', backgroundColor: '#f5f5f5', borderRadius: '4px' }} />}
                                </td>
                                <td style={{ ...td, maxWidth: '240px' }}>
                                    <span style={{ fontWeight: 500 }}>{order.title}</span>
                                </td>
                                <td style={{ ...td, textAlign: 'center' }}>{order.quantity}</td>
                                <td style={td}>{order.buyer}</td>
                                <td style={{ ...td, fontWeight: 600, color: '#00a650', whiteSpace: 'nowrap' }}>{fmt(order.total_amount)}</td>
                                <td style={td}>
                                    <span style={{ color: STATUS_ORDER[order.status]?.color || '#999', fontWeight: 500 }}>
                                        {STATUS_ORDER[order.status]?.label || order.status}
                                    </span>
                                </td>
                                <td style={td}>
                                    <span style={{ color: order.shipping_status === 'delivered' ? '#00a650' : '#666' }}>
                                        {SHIPPING_STATUS[order.shipping_status] || '—'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#2D3277', fontSize: '0.8rem' };
const td = { padding: '10px 12px', verticalAlign: 'middle' };
const filterBtn = (active) => ({
    padding: '5px 12px', borderRadius: '6px', border: '1px solid #ddd', cursor: 'pointer',
    fontSize: '0.8rem', backgroundColor: active ? '#2D3277' : 'white', color: active ? 'white' : '#333'
});
const dateInput = { padding: '5px 8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem' };

export default Vendas;
